import mixpanel from "./mixpanelClient";
import { createRecordingIdleDetector } from "./recordingIdleDetector";
import { swallowRecorderTeardown } from "./swallowRecorderTeardown";

/**
 * Analytics module for Mixpanel user identification and tracking.
 *
 * This module provides a thin abstraction over mixpanel-browser (via
 * `lib/mixpanelClient.ts`, the async-recorder entry point) to:
 * - Prevent duplicate identify() calls on re-renders
 * - Provide type-safe helpers for common operations
 * - Centralize analytics logic for easy testing and future extensibility
 *
 * Usage:
 *   import { identifyUser, resetAnalytics } from "@/lib/analytics";
 *
 * Note: Mixpanel is initialized in instrumentation-client.ts (required by Next.js).
 * This module works with the same global mixpanel instance.
 */

// Module-level state to prevent duplicate identification calls
let isIdentified = false;
let currentUserId: string | null = null;

/**
 * Check if Mixpanel has been initialized.
 * In environments like Storybook, instrumentation-client.ts doesn't run,
 * so mixpanel.init() is never called. Calling mixpanel methods before init()
 * causes "Cannot read properties of undefined" errors.
 *
 * Exported so other modules can gate on whether Mixpanel is recording — e.g.
 * `lib/toast.ts` escalates its console bridge to `console.error` only when
 * Mixpanel is live (its replay feed surfaces only error-level console lines).
 */
export function isMixpanelInitialized(): boolean {
  try {
    // Mixpanel sets config.token after init() - if it's falsy, not initialized
    // Using 'as any' because the config property exists at runtime but isn't in the type definitions
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Boolean((mixpanel as any).config?.token);
  } catch {
    return false;
  }
}

interface UserProperties {
  /**
   * User initials for privacy-safe identification in Mixpanel.
   * We use initials (e.g., "JS") instead of full names to avoid exposing PII.
   */
  $name: string;
  /**
   * The currently selected customer account ID.
   * A single person can have multiple accounts, so this tracks which one is active.
   */
  customerAccountId?: string;
}

/**
 * Identify a user in Mixpanel. Uses the user's stable UUID as the distinct_id.
 * Only calls identify() once per user to avoid duplicate API calls, but allows
 * people.set() to re-fire when properties change (e.g., account switch).
 *
 * @param userId - The user's stable UUID from userProfile.id
 * @param properties - User properties to set ($name should be initials for privacy)
 *
 * @example
 * identifyUser(userProfile.id, { $name: userProfile.initials }); // "JS" not "John Smith"
 */
export function identifyUser(userId: string, properties: UserProperties): void {
  // Skip if Mixpanel hasn't been initialized (e.g., in Storybook)
  if (!isMixpanelInitialized()) {
    return;
  }

  // Link prior anonymous events to this authenticated user via $device_id merge.
  // Only call identify() once per user to avoid duplicate API calls.
  if (!isIdentified || currentUserId !== userId) {
    mixpanel.identify(userId);
    isIdentified = true;
    currentUserId = userId;
  }

  // Always update profile properties — allows changes like account switches
  // to propagate without being blocked by the identify dedup guard
  mixpanel.people.set({
    ...properties,
    personId: userId,
  });
}

/**
 * Reset Mixpanel state on logout.
 * Generates a new anonymous device ID so the previous user's identity
 * doesn't leak — but tracking continues as a fresh anonymous visitor.
 *
 * Call this BEFORE redirecting to the logout page.
 */
export function resetAnalytics(): void {
  // Skip if Mixpanel hasn't been initialized (e.g., in Storybook)
  if (!isMixpanelInitialized()) {
    return;
  }

  mixpanel.reset();
  // reset() generates a new anonymous $device_id — the user continues to be
  // tracked as a fresh anonymous visitor (important for /register flow).
  isIdentified = false;
  currentUserId = null;
}

/**
 * Track a custom event with optional properties.
 * Use this for tracking specific user actions beyond autocapture.
 *
 * @param eventName - Name of the event (e.g., "Payment Initiated")
 * @param properties - Optional event properties
 *
 * @example
 * trackEvent("Payment Initiated", { amount: 100, currency: "GBP" });
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  // Skip if Mixpanel hasn't been initialized (e.g., in Storybook)
  if (!isMixpanelInitialized()) {
    return;
  }

  mixpanel.track(eventName, properties);
}

/**
 * Emit a queryable Mixpanel "Error Logged" event for a logged error/warning.
 *
 * Why this exists: every logged error already fans out to `console.*` (Sentry
 * breadcrumb, OTel/Kibana log, and the Mixpanel session-replay CONSOLE panel),
 * but the replay console is only readable by scrubbing an individual recording
 * — it is NOT a queryable event you can filter, break down, or alert on in the
 * Activity feed. A generic `"Toast Displayed"` event fires too, but it only
 * carries the user-facing toast copy ("Please try again"), never the real
 * cause. This surfaces the actual cause (status, exception type, origin) as a
 * first-class event so "how many DRAFT-state submit conflicts fired on
 * /onboarding/business/processing today?" is answerable in Mixpanel.
 *
 * PII-safety: callers pass only fixed-vocabulary/derived fields (HTTP status,
 * exception type, origin, pathname). `logError` already redacts its payload;
 * this event deliberately carries NO request/response bodies or query strings.
 *
 * No-ops when Mixpanel isn't initialized (SSR / Storybook / localhost) — same
 * guard as every other function here.
 */
export function trackLoggedError(
  level: "error" | "warning",
  message: string,
  properties?: Record<string, unknown>
): void {
  if (!isMixpanelInitialized()) {
    return;
  }

  mixpanel.track("Error Logged", { level, message, ...properties });
}

/**
 * Optimize session recording to reduce unnecessary capture during idle periods.
 *
 * - Pauses recording when the browser tab is hidden (visibilitychange API)
 * - Pauses recording after 2 minutes of no user interaction (custom idle detector)
 * - Resumes recording when the user returns or interacts
 *
 * Call once after mixpanel.init(). Safe to call in non-browser environments (no-ops).
 */
export function setupSessionRecordingOptimizations(): void {
  if (!isMixpanelInitialized() || typeof document === "undefined") {
    return;
  }

  // Pause recording when the tab is hidden, resume when visible
  // Both calls return the recorder's stop/flush promise — see swallowRecorderTeardown (CUSTOMER-UI-7J).
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      swallowRecorderTeardown(mixpanel.pause_session_recording());
    } else {
      swallowRecorderTeardown(mixpanel.resume_session_recording());
    }
  });

  // Pause recording after 2 minutes of no user interaction. Re-arming the idle
  // timer is throttled to once a second — mousemove/scroll fire far faster.
  const { onActivity: onUserActivity } = createRecordingIdleDetector({
    idleMs: 120_000,
    rearmThrottleMs: 1_000,
    onIdle: () => swallowRecorderTeardown(mixpanel.pause_session_recording()),
    onActive: () =>
      swallowRecorderTeardown(mixpanel.resume_session_recording()),
  });

  const activityEvents = [
    "mousemove",
    "keydown",
    "scroll",
    "click",
    "touchstart",
  ];
  activityEvents.forEach((event) => {
    document.addEventListener(event, onUserActivity, { passive: true });
  });

  onUserActivity();
}
